import { Box, Container, Typography } from '@mui/material';
import { IconAlertCircle } from '@tabler/icons-react';
import React from 'react';
import { StyledButton } from './ui';

/**
 * Error boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole app.
 *
 * @class ErrorBoundary
 * @augments React.Component
 */
class ErrorBoundary extends React.Component {
  /**
   * Constructor for ErrorBoundary
   * @param {Object} props - Component props
   */
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  /**
   * Static method called when an error is thrown during rendering
   * @returns {Object} New state object
   */
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  /**
   * Called when an error is caught by this error boundary
   * @param {Error} error - The error that was caught
   * @param {Object} errorInfo - Information about the component stack
   */
  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  /**
   * Refreshes the page to reset the error state
   */
  handleRefresh = () => {
    window.location.reload();
  };

  /**
   * Resets the error state to allow retry without refresh
   */
  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  /**
   * Renders the error boundary component
   * @returns {JSX.Element} The error UI or children components
   */
  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ffebee',
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <IconAlertCircle size={64} stroke={1.5} color="#d32f2f" />
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                  fontWeight: 700,
                  color: '#d32f2f',
                  mb: 2,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                Oops!
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                  fontWeight: 600,
                  color: 'var(--color-primary, #5e4d89)',
                  mb: 2,
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                Something Went Wrong
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  color: '#6c757d',
                  mb: 4,
                  maxWidth: '500px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                We encountered an unexpected error while loading this page. Our
                team has been notified and is working to resolve the issue.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  mb: 4,
                }}
              >
                <StyledButton
                  variant="contained"
                  size="large"
                  onClick={this.handleRetry}
                  sx={{
                    minWidth: '140px',
                    backgroundColor: 'var(--color-primary, #5e4d89)',
                    '&:hover': {
                      backgroundColor: 'var(--color-primary, #483b68)',
                    },
                  }}
                >
                  Try Again
                </StyledButton>

                <StyledButton
                  variant="outlined"
                  size="large"
                  onClick={this.handleRefresh}
                  sx={{
                    minWidth: '140px',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      borderColor: '#d32f2f',
                    },
                  }}
                >
                  Refresh Page
                </StyledButton>
              </Box>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box
                  sx={{
                    mt: 4,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    textAlign: 'left',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1, color: '#d32f2f' }}>
                    Error Details (Development Only):
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      mb: 2,
                    }}
                  >
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: '#666',
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  color: '#adb5bd',
                  fontSize: '0.875rem',
                }}
              >
                Error ID: ERR-{Date.now().toString().slice(-6)}
              </Typography>
            </Box>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
